export const aiTreeSlides = [
  {
    id: 'ai_intro',
    title: 'AI Foundations',
    type: 'text',
    physics: { enabled: false, mode: 'none', params: {} },
    timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
    children: [
      {
        id: 'ai_llm_stack',
        title: 'How LLM Apps Work',
        type: 'diagram',
        physics: { enabled: true, mode: 'spring_expand', params: { damping: 0.84 } },
        timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
        children: [],
      },
      {
        id: 'ai_rag_flow',
        title: 'RAG Retrieval Flow',
        type: 'split',
        physics: { enabled: true, mode: 'force_graph', params: { damping: 0.87 } },
        timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
        children: [],
      },
      {
        id: 'ai_agent_orchestration',
        title: 'Agent Orchestration',
        type: 'simulation',
        physics: { enabled: true, mode: 'gravity_reveal', params: { gravity: 9.81 } },
        timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 12 },
        children: [],
      },
    ],
  },
];

export const aiNodeContent = {
  ai_intro: {
    topicId: 'ai_intro',
    explanation:
      'AI is becoming a basic literacy layer for work, education, and civic life. People who can collaborate with AI will adapt faster, while those who do not may lose agency in decisions shaped by automated systems.',
    keyPoints: [
      'AI fluency is now part of staying economically relevant',
      'Understanding AI helps you question, verify, and lead, not just consume',
      'Learning AI early compounds into long-term opportunity',
    ],
    examples: [
      'Career transitions accelerated by AI skills',
      'Daily decisions improved by AI-assisted reasoning',
    ],
    bridgeFromPrevious: 'A starting point for long-term relevance in a changing world.',
    bridgeToNext: 'Next we map how AI systems actually work so you can use them with confidence.',
    demoPlan: {
      kind: 'PEDAGOGICAL_DIAGRAM',
      reason: 'Show core AI system components as a concept graph.',
      animationEmphasis: 'reveal',
    },
    pedagogicalDiagram: {
      variant: 'graph',
      title: 'Core AI System Components',
      subtitle: 'Model + retrieval + tools + memory + guardrails',
      durationSec: 6,
      graph: {
        nodes: [
          { id: 'm', label: 'LLM', xk: 0.5, yk: 0.2 },
          { id: 'r', label: 'Retrieval', xk: 0.24, yk: 0.48 },
          { id: 't', label: 'Tools', xk: 0.5, yk: 0.48 },
          { id: 'mem', label: 'Memory', xk: 0.76, yk: 0.48 },
          { id: 'g', label: 'Guardrails', xk: 0.5, yk: 0.78 },
        ],
        edges: [
          { from: 'r', to: 'm', label: 'context', directed: true },
          { from: 't', to: 'm', label: 'actions', directed: true },
          { from: 'mem', to: 'm', label: 'history', directed: true },
          { from: 'm', to: 'g', label: 'safe output', directed: true },
        ],
      },
    },
    scene: {
      layout: { mode: 'full_text', params: { emphasis: 'overview' } },
      animationPlan: [
        { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.3 },
        { step: 2, target: 'body', effect: 'typewriter', durationSec: 1.0 },
      ],
      artifacts: [
        {
          id: 'foundation_model_box',
          primitive: 'rect',
          params: { xk: 0.72, yk: 0.2, wk: 0.22, hk: 0.12, lineWidth: 1.8 },
        },
        {
          id: 'retrieval_box',
          primitive: 'rect',
          params: { xk: 0.72, yk: 0.38, wk: 0.22, hk: 0.12, lineWidth: 1.8 },
        },
        {
          id: 'tool_box',
          primitive: 'rect',
          params: { xk: 0.72, yk: 0.56, wk: 0.22, hk: 0.12, lineWidth: 1.8 },
        },
        {
          id: 'signal_flow',
          primitive: 'polyline',
          params: {
            points: [
              { xk: 0.62, yk: 0.26 },
              { xk: 0.72, yk: 0.26 },
              { xk: 0.72, yk: 0.44 },
              { xk: 0.72, yk: 0.62 },
            ],
            lineWidth: 1.4,
          },
        },
      ],
      timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
      physics: { enabled: false, mode: 'none', params: {} },
      layers: {
        background: true,
        content: true,
        highlight: true,
        transition: true,
        debug: false,
      },
    },
  },
  ai_llm_stack: {
    topicId: 'ai_llm_stack',
    explanation:
      'If AI is shaping your future, you should know what happens between your prompt and the final answer. This pipeline view turns AI from a black box into a system you can steer.',
    keyPoints: [
      'Prompt structure changes output quality and bias',
      'Inference is only one stage in reliable AI behavior',
      'Safety and validation are part of responsible AI use',
    ],
    examples: [
      'High-stakes business answers with traceable steps',
      'Tutoring systems that explain instead of guessing',
    ],
    bridgeFromPrevious: 'From existential urgency to practical system understanding.',
    bridgeToNext: 'Then we ground AI answers in evidence with retrieval.',
    demoPlan: {
      kind: 'PEDAGOGICAL_DIAGRAM',
      reason: 'Pipeline semantics are clearer as a stage table.',
      animationEmphasis: 'progressive_draw',
    },
    pedagogicalDiagram: {
      variant: 'table',
      title: 'LLM App Runtime Stages',
      subtitle: 'What each stage does in production',
      durationSec: 6,
      table: {
        headers: ['Stage', 'Role', 'Failure Risk'],
        rows: [
          ['Input', 'Capture user intent', 'Ambiguous question'],
          ['Prompt Build', 'Inject context + instructions', 'Missing constraints'],
          ['Inference', 'Generate candidate output', 'Hallucination'],
          ['Guardrail', 'Safety + policy checks', 'False pass/block'],
          ['Formatter', 'Structured response', 'Schema mismatch'],
        ],
        caption: 'Reliability comes from the full chain, not model call alone.',
      },
    },
    sketchPath: {
      title: 'LLM Runtime Pipeline',
      nodes: [
        { id: 'input', label: 'User Input', xk: 0.12, yk: 0.45 },
        { id: 'prompt', label: 'Prompt Builder', xk: 0.34, yk: 0.45 },
        { id: 'model', label: 'LLM', xk: 0.56, yk: 0.45 },
        { id: 'output', label: 'Response', xk: 0.8, yk: 0.45 },
      ],
      edges: [
        { from: 'input', to: 'prompt', label: 'intent' },
        { from: 'prompt', to: 'model', label: 'context window' },
        { from: 'model', to: 'output', label: 'tokens' },
      ],
      draw: { secPerEdge: 0.45, secPerNode: 0.25, showArrowheads: true },
    },
    scene: {
      layout: { mode: 'split', params: { left: 'concept', right: 'transform' } },
      animationPlan: [
        { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.25 },
        { step: 2, target: 'left_text', effect: 'typewriter', durationSec: 0.9 },
        { step: 3, target: 'right_demo', effect: 'draw', durationSec: 1.0 },
      ],
      artifacts: [
        {
          id: 'runtime_lane',
          primitive: 'line',
          params: { x1k: 0.52, y1k: 0.72, x2k: 0.9, y2k: 0.72, lineWidth: 1.6 },
        },
        {
          id: 'inference_token',
          primitive: 'circle',
          params: { xk: 0.56, yk: 0.72, rk: 0.012, lineWidth: 1.4, fill: 'rgba(245,250,255,0.14)' },
          motion: {
            type: 'light_pulse',
            durationSec: 3.2,
            loop: true,
            x1k: 0.56,
            y1k: 0.72,
            x2k: 0.88,
            y2k: 0.72,
            cyclesPerSec: 0.9,
          },
        },
      ],
      timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
      physics: { enabled: true, mode: 'spring_expand', params: { damping: 0.84 } },
      layers: {
        background: true,
        content: true,
        highlight: true,
        transition: true,
        debug: false,
      },
    },
  },
  ai_rag_flow: {
    topicId: 'ai_rag_flow',
    explanation:
      'In a world flooded with synthetic content, truth matters. RAG helps AI connect answers to real sources, so learners can trust what they use to make decisions.',
    keyPoints: [
      'Grounding reduces hallucination risk',
      'Evidence-linked answers support better judgment',
      'Source-aware AI is critical for credible learning',
    ],
    examples: ['Policy interpretation with citations', 'Technical support answers tied to docs'],
    bridgeFromPrevious: 'After understanding the pipeline, we add trust through evidence.',
    bridgeToNext: 'Finally we coordinate specialized agents for complex real-world tasks.',
    demoPlan: {
      kind: 'PEDAGOGICAL_DIAGRAM',
      reason: 'RAG quality is easiest to see as groundedness comparison.',
      animationEmphasis: 'progressive_draw',
    },
    pedagogicalDiagram: {
      variant: 'chart',
      title: 'Answer Grounding With and Without RAG',
      subtitle: 'Groundedness score by query type',
      durationSec: 6,
      chart: {
        type: 'bar',
        labels: ['Policy', 'Docs Q&A', 'Troubleshooting', 'Compliance'],
        series: [
          { name: 'No RAG', values: [0.42, 0.48, 0.39, 0.35] },
          { name: 'With RAG', values: [0.82, 0.88, 0.79, 0.84] },
        ],
      },
    },
    sketchPath: {
      title: 'RAG Flow',
      nodes: [
        { id: 'query', label: 'Query', xk: 0.14, yk: 0.5 },
        { id: 'retriever', label: 'Retriever', xk: 0.36, yk: 0.5 },
        { id: 'kb', label: 'Vector DB', xk: 0.56, yk: 0.32 },
        { id: 'generator', label: 'Generator', xk: 0.56, yk: 0.68 },
        { id: 'answer', label: 'Answer', xk: 0.8, yk: 0.5 },
      ],
      edges: [
        { from: 'query', to: 'retriever', label: 'embed/query' },
        { from: 'retriever', to: 'kb', label: 'top-k fetch' },
        { from: 'kb', to: 'generator', label: 'evidence' },
        { from: 'generator', to: 'answer', label: 'grounded output' },
      ],
      draw: { secPerEdge: 0.4, secPerNode: 0.24, showArrowheads: true },
    },
    scene: {
      layout: { mode: 'canvas_based_dynamic', params: { focus: 'rag' } },
      animationPlan: [
        { step: 1, target: 'nodes', effect: 'stagger_in', durationSec: 0.6 },
        { step: 2, target: 'edges', effect: 'progressive_draw', durationSec: 1.1 },
      ],
      artifacts: [
        {
          id: 'retrieval_wavefront',
          primitive: 'circle',
          params: { xk: 0.36, yk: 0.5, rk: 0.01, lineWidth: 1.2 },
          motion: {
            type: 'wavefront_expand',
            durationSec: 3,
            loop: true,
            minRk: 0.01,
            maxRk: 0.18,
            expandSec: 2.6,
            alphaBase: 0.85,
          },
        },
      ],
      timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
      physics: { enabled: true, mode: 'force_graph', params: { damping: 0.87 } },
      layers: {
        background: true,
        content: true,
        highlight: true,
        transition: true,
        debug: false,
      },
    },
  },
  ai_agent_orchestration: {
    topicId: 'ai_agent_orchestration',
    explanation:
      'The future of learning and work is collaborative intelligence: humans plus coordinated AI agents. Knowing this orchestration model helps you design, supervise, and improve systems instead of being replaced by them.',
    keyPoints: [
      'Specialized agents scale complex problem-solving',
      'Human oversight stays central to safe automation',
      'Continuous learning is the edge in a fast-moving AI economy',
    ],
    examples: [
      'Research copilots with planner-executor-verifier loops',
      'Operations workflows with auditable AI handoffs',
    ],
    bridgeFromPrevious: 'Grounded generation now expands into orchestrated intelligence.',
    bridgeToNext:
      'If this is where the world is going, the next step is to build your own path: sign up, pick your track, and keep learning with adaptive AI lessons.',
    demoPlan: {
      kind: 'PEDAGOGICAL_DIAGRAM',
      reason: 'Agent coordination is best conveyed as explicit handoff dialogue.',
      animationEmphasis: 'progressive_draw',
    },
    pedagogicalDiagram: {
      variant: 'chat',
      title: 'Orchestrator-Agent Handoff',
      subtitle: 'Planner, executor, and verifier interaction',
      durationSec: 6,
      chat: {
        participants: [
          { id: 'admin', name: 'Orchestrator Admin', xk: 0.18 },
          { id: 'planner', name: 'Planner', xk: 0.42 },
          { id: 'executor', name: 'Executor', xk: 0.62 },
          { id: 'verifier', name: 'Verifier', xk: 0.82 },
        ],
        messages: [
          { speakerId: 'admin', text: 'Planner, break this into steps.', atSec: 0.4 },
          { speakerId: 'planner', text: 'Plan ready: retrieve, execute, validate.', atSec: 1.2 },
          { speakerId: 'admin', text: 'Executor, run tools for step 2.', atSec: 2.0 },
          { speakerId: 'executor', text: 'Execution complete. Returning result.', atSec: 2.8 },
          { speakerId: 'admin', text: 'Verifier, confirm policy + accuracy.', atSec: 3.6 },
          { speakerId: 'verifier', text: 'Validated. Safe to deliver.', atSec: 4.4 },
        ],
      },
    },
    sketchPath: {
      title: 'Agent Orchestration Graph',
      nodes: [
        { id: 'orchestrator', label: 'Orchestrator', xk: 0.5, yk: 0.2 },
        { id: 'planner', label: 'Planner', xk: 0.26, yk: 0.5 },
        { id: 'executor', label: 'Executor', xk: 0.5, yk: 0.5 },
        { id: 'verifier', label: 'Verifier', xk: 0.74, yk: 0.5 },
        { id: 'deliver', label: 'Deliver', xk: 0.5, yk: 0.82 },
      ],
      edges: [
        { from: 'orchestrator', to: 'planner', label: 'plan task' },
        { from: 'orchestrator', to: 'executor', label: 'run tools' },
        { from: 'orchestrator', to: 'verifier', label: 'check output' },
        { from: 'planner', to: 'deliver', label: 'strategy' },
        { from: 'executor', to: 'deliver', label: 'result' },
        { from: 'verifier', to: 'deliver', label: 'validated result' },
      ],
      draw: { secPerEdge: 0.35, secPerNode: 0.22, showArrowheads: true },
    },
    scene: {
      layout: { mode: 'canvas_based_dynamic', params: { focus: 'orchestration' } },
      animationPlan: [
        { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.2 },
        { step: 2, target: 'graph', effect: 'draw', durationSec: 1.2 },
      ],
      artifacts: [
        {
          id: 'orchestration_baseline',
          primitive: 'line',
          params: { x1k: 0.12, y1k: 0.9, x2k: 0.88, y2k: 0.9, lineWidth: 2.0 },
        },
      ],
      timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 12 },
      physics: { enabled: true, mode: 'gravity_reveal', params: { gravity: 9.81 } },
      layers: {
        background: true,
        content: true,
        highlight: true,
        transition: true,
        debug: false,
      },
    },
  },
};

// Backward-compatible exports while old imports are migrated.
export const relativityTreeSlides = aiTreeSlides;
export const relativityNodeContent = aiNodeContent;
export const langchainTreeSlides = aiTreeSlides;
export const langchainNodeContent = aiNodeContent;
